import { Component, Directive, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LxmDialog } from 'src/app/_helpers';
import { Status, UserAction } from 'src/app/enum';
import { ClientsService, IClientCard, IClientContactListItem, IClientFormData, IClientInfo } from 'src/app/_services/clients.service';
import { ClientState } from '../../state/client.state';
import { appSettings } from 'src/app/app.settings';
import { AddContactDialogComponent } from './dialog/add-contact/add-contact.dialog';

@Component({
  selector: 'client-contacts-card',
  templateUrl: './client-contacts.card.html',
  styleUrls: ['./client-contacts.card.scss']
})
export class ClientContactsCard {
  @Input() public clientId: string;

  public showOfficialContacts: boolean;

  constructor(private _route: ActivatedRoute) {
    const { officialContacts } = this._route.snapshot.data.initialData;
    this.showOfficialContacts = officialContacts?.items?.length > 0;
  }
}

@Directive()
export abstract class ContactsListBase {

  @Input() public clientId: string;

  public displayedColumns: string[] = ['name', 'role', 'portfolioDescription', 'email', 'phoneNumber'];
  public dataUrl;

  public clientCard: IClientCard;
  public clientInfo: IClientInfo;
  public formData: IClientFormData;
  public UserAction = UserAction;

  public contacts: IClientContactListItem[];

  public phoneCodes = [];

  constructor(
    protected _route: ActivatedRoute,
    protected _dialog: LxmDialog,
    protected _clientsService: ClientsService,
    protected _state: ClientState,
    protected _translateService: TranslateService
  ) {
    const { clientCard } = this._route.snapshot.data.initialData;

    this.clientCard = clientCard;
    this.contacts = this.initContacts();

    if (!this.clientCard) {
      return;
    }

    this.formData = this.clientCard.formData;
    this.clientInfo = this.clientCard.clientInfo;
    this.phoneCodes = this.formData?.phoneCodes;
  }

  public showAllContacts = false;

  public toggleShowAllContacts(toSet = !this.showAllContacts) {
    this.showAllContacts = toSet;
  }

  public getPhoneCodeById(id: string) {
    return this.phoneCodes?.find(x => x.id === id)?.value;
  }

  public get isSelfSupplier() {
    return this._state.isSelfSupplier;
  }

  public get isClient() {
    return this._state.isClient;
  }

  public get hasContacts() {
    return this.contacts?.length > 0;
  }

  public abstract get canEdit();

  public abstract getCardTitle();

  public abstract getContacts();

  public abstract getContactIcon();

  protected abstract initContacts();

  public get contactsCount() {
    if (!this.contacts) return 0;
    return this.contacts.length;
  }

  public openDialog(row?: IClientContactListItem) {

    this._dialog.open(AddContactDialogComponent, {
      ...appSettings.DIALOG_SIZES.M,
      data: {
        clientId: this.clientId,
        formData: this.formData,
        contact: row,
        edit: this.canEdit
      }
    }, res => {
      this.getContacts();
    });
  }

  // Contact display helpers
  public getContactPhoneCode(contact: any) {
    return contact?.phoneCode?.value;
  }

  public getContactPhoneShort(contact: any) {
    return contact?.phoneNumberShort;
  }

  public getContactStatusClass(contact: any) {
    if (!contact) return;
    if (contact.isLinked) return 'status-linked';
    if (contact.status === Status.Active) return 'status-active';
    if (contact.status === Status.Disabled) return 'status-disabled';
  }

  public getTranslatedPortfolioDescription(contact: any) {
    if (!contact) {
      return null;
    }

    const lang = this._translateService.currentLang;

    const { translatedPortfolioDescription, translatedPortfolioDescriptionLanguageOrder, portfolioDescription } = contact || {};

    if (translatedPortfolioDescription) {

      if (translatedPortfolioDescription[lang]) {
        return contact.translatedPortfolioDescription[lang];
      }

      for (const l of translatedPortfolioDescriptionLanguageOrder) {
        if (translatedPortfolioDescription[l]) {
          return translatedPortfolioDescription[l];
        }
      }
    }

    return portfolioDescription;
  }

}

@Component({
  selector: 'own-contacts-list',
  templateUrl: './components/contacts-list/contacts-list.component.html',
  styleUrls: ['./components/contacts-list/contacts-list.component.scss']
})
export class OwnContactsList extends ContactsListBase {

  constructor(
    _route: ActivatedRoute,
    _dialog: LxmDialog,
    _clientsService: ClientsService,
    _state: ClientState,
    _translateService: TranslateService
  ) {
    super(_route, _dialog, _clientsService, _state, _translateService);
  }

  public getContactIcon() {
    return "contact-status-icon";
  }

  public getCardTitle() {
    return "own_contacts_title";
  }

  public getContacts() {
    const clientId = this.clientInfo?.id;
    if (!clientId) return;
    this._clientsService.getOwnClientContacts(clientId).result(null, res => {
      const items = res.items;
      if (items) {
        this.contacts = items;
      }
    })
  }

  protected initContacts() {
    const { ownContacts } = this._route.snapshot.data.initialData;
    return ownContacts?.items;
  }

  public get canEdit(): boolean {
    return this._state.canEdit;
  }
}

@Component({
  selector: 'official-contacts-list',
  templateUrl: './components/contacts-list/contacts-list.component.html',
  styleUrls: ['./components/contacts-list/contacts-list.component.scss']
})
export class OfficialContactsList extends ContactsListBase {

  constructor(
    _route: ActivatedRoute,
    _dialog: LxmDialog,
    _clientsService: ClientsService,
    _state: ClientState,
    _translateService: TranslateService
  ) {
    super(_route, _dialog, _clientsService, _state, _translateService);
  }

  public getContactIcon() {
    return "profile-status-icon";
  }

  public getCardTitle() {
    return "official_contacts_title";
  }

  public getContacts() {
    const clientId = this.clientInfo?.id;
    if (!clientId) return;
    this._clientsService.getOfficialClientContacts(clientId).result(null, res => {
      const items = res.items;
      if (items) {
        this.contacts = items;
      }
    })
  }

  protected initContacts() {
    const { officialContacts } = this._route.snapshot.data.initialData;
    return officialContacts?.items;
  }

  public get canEdit(): boolean {
    return false;
  }
}

